import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { ResponsiveBar } from "@nivo/bar";
import { Table } from "semantic-ui-react";
import {
  CHART_THEME,
  LEGEND_CONFIG,
  renderTruncatedText,
  createFocusableBarLayer,
  getChartContainerProps,
  getAccessibleTextColor
} from "./chartUtils";

const StackedBarByDepartment = ({
  barGraphDataByDepartment,
  statusKeys,
  handleChartClick,
  graphTextColor,
  showDataTables,
  styles
}) => {
  const totalRecommendations = _.sumBy(barGraphDataByDepartment, "totalCount");

  const ariaLabel = `Interactive bar chart showing ${totalRecommendations} recommendations by department and status. Tab through individual bars to filter data. Press enter to populate the Recommendation List with corresponding bar graph data segment.`;

  return (
    <>
      <div {...getChartContainerProps()} aria-label={ariaLabel}>
        <ResponsiveBar
          data={barGraphDataByDepartment}
          labelTextColor={({ color }) =>
            color ? getAccessibleTextColor(color) : graphTextColor || "#000000"
          }
          keys={statusKeys}
          layout="horizontal"
          indexBy="department"
          margin={{ top: 10, right: 170, bottom: 50, left: 300 }}
          padding={0.2}
          colors={({ id, data }) => data.colors[id]}
          borderWidth={1.5}
          theme={CHART_THEME}
          legends={[LEGEND_CONFIG]}
          animate
          motionStiffness={90}
          motionDamping={15}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "# Recommendations",
            legendPosition: "middle",
            legendOffset: 40
          }}
          enableGridY={false}
          axisLeft={{
            renderTick: d => renderTruncatedText(d, 35)
          }}
          onClick={e => handleChartClick(e, "status", "department")}
          layers={[
            "grid",
            "axes",
            "bars",
            "markers",
            "legends",
            "annotations",
            createFocusableBarLayer(
              statusKeys,
              handleChartClick,
              "status",
              "department",
              {
                sortBy: [
                  bar =>
                    barGraphDataByDepartment
                      .map(item => item.id)
                      .indexOf(bar.data.indexValue)
                ],
                order: ["desc"]
              }
            )
          ]}
        />
      </div>
      <div css={styles} className={showDataTables ? "" : "sr-only"}>
        <Table
          id="department-chart-data-table"
          aria-label="Recommendations data by department and status"
          basic="very"
          celled
          compact
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                colSpan={statusKeys.length + 2}
                textAlign="center"
                style={{ fontWeight: "bold" }}
                scope="col"
              >
                Detailed Breakdown of {totalRecommendations} Recommendations by
                Department and Status
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell scope="col">Department</Table.HeaderCell>
              {statusKeys.map(status => (
                <Table.HeaderCell scope="col" key={status} textAlign="center">
                  {status}
                </Table.HeaderCell>
              ))}
              <Table.HeaderCell textAlign="center">Total</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {barGraphDataByDepartment.map(deptData => {
              const deptTotal = statusKeys.reduce(
                (sum, status) => sum + (deptData[status] || 0),
                0
              );
              return (
                <Table.Row key={deptData.department}>
                  <Table.HeaderCell scope="row" textAlign="left">
                    {deptData.department}
                  </Table.HeaderCell>
                  {statusKeys.map(status => (
                    <Table.Cell key={status} textAlign="center">
                      {deptData[status] || 0}
                    </Table.Cell>
                  ))}
                  <Table.Cell textAlign="center">{deptTotal}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </>
  );
};

StackedBarByDepartment.propTypes = {
  barGraphDataByDepartment: PropTypes.arrayOf(
    PropTypes.shape({
      department: PropTypes.string.isRequired
    })
  ).isRequired,
  statusKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleChartClick: PropTypes.func.isRequired,
  graphTextColor: PropTypes.string,
  showDataTables: PropTypes.bool.isRequired,
  styles: PropTypes.shape({})
};

StackedBarByDepartment.defaultProps = {
  graphTextColor: null,
  styles: {}
};

export default StackedBarByDepartment;
